import * as React from "react";
import {
  AuthUser,
  confirmSignIn,
  getCurrentUser,
  signIn,
  signOut,
  type UserAttributeKey,
} from "aws-amplify/auth";

export function authErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return "Something went wrong.";
}

export type LoginPhaseResult =
  | { phase: "signed_in" }
  | {
      phase: "new_password_required";
      missingAttributes: readonly UserAttributeKey[];
    };

export interface AuthContext {
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<LoginPhaseResult>;
  completeNewPassword: (
    newPassword: string,
    userAttributes?: Partial<Record<UserAttributeKey, string>>
  ) => Promise<void>;
  logout: () => Promise<void>;
  user: AuthUser | null;
}

const AuthContext = React.createContext<AuthContext | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const isAuthenticated = !!user;

  const logout = React.useCallback(async () => {
    await signOut();
    setUser(null);
  }, []);

  const login = React.useCallback(
    async (username: string, password: string): Promise<LoginPhaseResult> => {
      const result = await signIn({ username, password });
      if (result.isSignedIn) {
        const current = await getCurrentUser();
        setUser(current);
        return { phase: "signed_in" };
      }
      if (
        result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        return {
          phase: "new_password_required",
          missingAttributes: result.nextStep.missingAttributes ?? [],
        };
      }
      throw new Error(
        "Sign-in could not be completed. MFA and other verification steps are not supported in this app yet."
      );
    },
    []
  );

  const completeNewPassword = React.useCallback(
    async (
      newPassword: string,
      userAttributes?: Partial<Record<UserAttributeKey, string>>
    ) => {
      const hasAttrs =
        userAttributes && Object.keys(userAttributes).length > 0;
      const result = await confirmSignIn({
        challengeResponse: newPassword,
        options: hasAttrs ? { userAttributes } : undefined,
      });
      if (result.isSignedIn) {
        const current = await getCurrentUser();
        setUser(current);
        return;
      }
      throw new Error(
        `Sign-in could not be completed after password change (${result.nextStep.signInStep}).`
      );
    },
    []
  );

  React.useEffect(() => {
    getCurrentUser()
      .then((current) => setUser(current))
      .catch(() => setUser(null));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        completeNewPassword,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
